import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Alert } from "react-bootstrap";
import { authAPI, setAuthToken, setUser, getAuthMode } from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";
import TwoFAInput from "./TwoFAInput";
import SignInForm from "./SignInForm";
import SignInAuthenticator from "./SignInAuthenticator";
import SetupFor2FA from "./SetupFor2FA";
import ForceChangePassword from "./ForceChangePassword";

interface SignInProps {
  onLoginSuccess: () => void;
}

type Phase =
  | "loading" // detecting auth mode
  | "identifier" // authenticator mode: enter employee ID
  | "form" // password mode: full form
  | "totp" // enter TOTP code (both modes)
  | "setup" // authenticator mode: must set up 2FA first
  | "force_change" // password mode: must change password on first login
  | "done";

const SignIn: React.FC<SignInProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [phase, setPhase] = useState<Phase>("loading");
  const [authMode, setAuthMode] = useState<"password" | "authenticator">(
    "password",
  );
  const [credentials, setCredentials] = useState({
    identifier: "",
    password: "",
  });
  const [setupToken, setSetupToken] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [sessionExpired] = useState(
    () => new URLSearchParams(window.location.search).get("expired") === "1",
  );
  const [setupUserId, setSetupUserId] = useState(0);

  useEffect(() => {
    getAuthMode().then((mode) => {
      setAuthMode(mode);
      setPhase(mode === "authenticator" ? "identifier" : "form");
    });
  }, []);

  // ── Password mode: step 1 ──────────────────────────────────────────────────
  const handlePasswordLogin = async (identifier: string, password: string) => {
    const response = await authAPI.login(identifier, password);
    if (response.requires_2fa) {
      setCredentials({ identifier, password });
      setPhase("totp");
    } else if (response.token && response.user) {
      setAuthToken(response.token);
      setUser(response.user);
      if (response.must_change_password) {
        setCurrentPassword(password);
        setPhase("force_change");
      } else {
        onLoginSuccess();
        navigate("/dashboard");
      }
    } else {
      throw new Error("Unexpected response from server");
    }
  };

  // ── Authenticator mode: step 1 — just the employee ID ─────────────────────
  const handleIdentifierSubmit = async (identifier: string) => {
    // Send ID with empty password — backend will ignore password in authenticator mode
    const response = await authAPI.login(identifier, "");
    if (response.requires_setup) {
      // First login — must set up authenticator
      setSetupToken(response.setup_token);
      setSetupUserId(response.user_id);
      setCredentials({ identifier, password: "" });
      setPhase("setup");
    } else if (response.requires_2fa) {
      setCredentials({ identifier, password: "" });
      setPhase("totp");
    } else if (response.token && response.user) {
      // Shouldn't happen in authenticator mode, but handle it
      setAuthToken(response.token);
      setUser(response.user);
      onLoginSuccess();
      navigate("/dashboard");
    }
  };

  // ── TOTP verification (both modes) ────────────────────────────────────────
  const handleVerify2FA = async (code: string) => {
    const response = await authAPI.login(
      credentials.identifier,
      credentials.password,
      code,
    );
    if (response.token && response.user) {
      setAuthToken(response.token);
      setUser(response.user);
      if (response.must_change_password) {
        setCurrentPassword(credentials.password);
        setPhase("force_change");
      } else {
        onLoginSuccess();
        navigate("/dashboard");
      }
    } else {
      throw new Error("Invalid authenticator code");
    }
  };

  const handleBack = () => {
    setPhase(authMode === "authenticator" ? "identifier" : "form");
    setCredentials({ identifier: "", password: "" });
  };

  // After 2FA setup is complete, send user back to the identifier screen to log in properly
  const handleSetupComplete = () => {
    setPhase("identifier");
    setCredentials({ identifier: "", password: "" });
  };

  if (phase === "loading") return null;

  // ForceChangePassword needs its own full-width layout, not the narrow login column
  if (phase === "force_change") {
    return (
      <ForceChangePassword
        currentPassword={currentPassword}
        onSuccess={() => {
          onLoginSuccess();
          navigate("/dashboard");
        }}
      />
    );
  }

  return (
    <Container
      fluid
      className="min-vh-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      <Row className="w-100 justify-content-center px-3">
        <Col xs={12} sm={10} md={6} lg={4} xl={3}>
          {sessionExpired && (
            <Alert variant="warning" className="mb-3 small text-center py-2">
              ⏱ {t("sessionExpired")}
            </Alert>
          )}
          {phase === "form" && <SignInForm onSubmit={handlePasswordLogin} />}
          {phase === "identifier" && (
            <SignInAuthenticator onSubmit={handleIdentifierSubmit} />
          )}
          {phase === "totp" && (
            <TwoFAInput onVerify={handleVerify2FA} onBack={handleBack} />
          )}
          {phase === "setup" && (
            <SetupFor2FA
              setupToken={setupToken}
              userId={setupUserId}
              onComplete={handleSetupComplete}
              onBack={handleBack}
            />
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SignIn;
